import React from 'react'
import {Box, Button, Typography} from '@mui/material'
import {useTranslation} from 'react-i18next'

/**
 * Fallback shown when a child route throws during render. Kept as a small
 * functional component so it can use i18n (the boundary itself must be a class).
 */
function ErrorFallback() {
  const {t} = useTranslation()
  return (
    <Box
      role="alert"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        minHeight: '60vh',
        px: 3,
        textAlign: 'center',
      }}
    >
      <Typography variant="h6">
        {t('errorBoundary.title', 'Etwas ist schiefgelaufen.')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{maxWidth: 480}}>
        {t('errorBoundary.message', 'Diese Seite konnte nicht angezeigt werden. Bitte laden Sie die Seite neu. Falls das Problem bestehen bleibt, wenden Sie sich an den Support.')}
      </Typography>
      <Button variant="contained" onClick={() => window.location.reload()}>
        {t('errorBoundary.reload', 'Neu laden')}
      </Button>
    </Box>
  )
}

interface ErrorBoundaryProps {
  children: React.ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
}

/**
 * Catches render/lifecycle exceptions in the wrapped subtree and shows a
 * contained fallback instead of letting the whole React tree unmount (which
 * leaves a blank/black screen). Navigation in this app is via full-page anchor
 * loads, so the boundary resets naturally on every page change.
 */
export default class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {hasError: false}
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return {hasError: true}
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Surface the real cause in the browser console for diagnosis.
    console.error('Unhandled error caught by ErrorBoundary:', error, info)
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback/>
    }
    return this.props.children
  }
}
